
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Building2, User, MapPin, FileText, Upload, CheckCircle2, Compass, Info } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { Link } from 'wouter';
import logoImage from "@assets/openbox_1762897846924.png";
import { GRADE_DEFECT_CEILINGS } from '@shared/grading';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
];

const businessSignupSchema = z.object({
  accountType: z.literal('BUSINESS'),
  // Company Information
  legalCompanyName: z.string().min(2, 'Legal company name is required'),
  dbaName: z.string().optional(),
  ein: z.string().regex(/^\d{2}-\d{7}$/, 'EIN must be in format XX-XXXXXXX').optional().or(z.literal('')),
  companyType: z.enum(['llc', 'corporation', 's-corp', 'partnership', 'sole-proprietorship', 'other']),
  
  // Primary Contact
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().min(10, 'Phone number is required'),
  
  // Corporate Address
  addressLine1: z.string().min(1, 'Street address is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(2, 'State is required'),
  zipCode: z.string().min(5, 'ZIP code is required'),
  
  // Delivery Equipment Options
  requiresLiftgate: z.boolean(),
  hasLoadingDock: z.boolean(),
  hasForklift: z.boolean(),
  
  // Use corporate address as ship-to
  useCorporateAsShipTo: z.boolean().optional(),
  
  // Company Profile
  employeeCount: z.enum(['1-10', '10-50', '50-100', '100+']),
  businessType: z.enum(['retailer', 'distributor', 'exporter', 'wholesaler', 'e-commerce', 'other']),
  annualRevenue: z.enum(['under-1m', '1m-5m', '5m-10m', '10m-50m', '50m+']),
  
  // Sales Tax Exemption - all three required if any are provided
  salesTaxExemptState: z.string().optional(),
  salesTaxExemptId: z.string().optional(),
  salesTaxExemptFile: z.instanceof(File).optional(),
  
  // Business Justification
  resaleStrategy: z.string().min(20, 'Please describe your resale strategy in at least 20 characters'),
  hasPhysicalStore: z.boolean(),
  storePhotoFile: z.instanceof(File).optional(),
  whyBusinessAccount: z.string().min(20, 'Please explain why you want a business account in at least 20 characters'),

  // Grading scale acknowledgement
  gradingScaleAcknowledged: z.literal(true, {
    errorMap: () => ({ message: 'You must acknowledge the cosmetic grading scale' }),
  }),
});

const consumerSignupSchema = z.object({
  accountType: z.literal('CONSUMER'),
  // Personal Information
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().min(10, 'Phone number is required'),
  
  // Residential Address
  addressLine1: z.string().min(1, 'Street address is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(2, 'State is required'),
  zipCode: z.string().min(5, 'ZIP code is required'),
});

const signupSchema = z.discriminatedUnion('accountType', [
  businessSignupSchema,
  consumerSignupSchema,
]);

type SignupFormData = z.infer<typeof signupSchema>;

export default function SignupPage() {
  console.log('[SignupPage] Component mounted - this should appear in console');
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [storePhotoFile, setStorePhotoFile] = useState<File | null>(null);
  const [accountType, setAccountType] = useState<'BUSINESS' | 'CONSUMER' | null>(null);

  // Auto-select business account type from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    if (type === 'business') {
      setAccountType('BUSINESS');
    } else if (type === 'consumer') {
      setAccountType('CONSUMER');
    }
  }, []);

  const businessForm = useForm<z.infer<typeof businessSignupSchema>>({
    resolver: zodResolver(businessSignupSchema),
    defaultValues: {
      accountType: 'BUSINESS',
      requiresLiftgate: true,
      hasLoadingDock: false,
      hasForklift: false,
      useCorporateAsShipTo: false,
      legalCompanyName: '',
      dbaName: '',
      ein: '',
      companyType: undefined,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      employeeCount: undefined,
      businessType: undefined,
      annualRevenue: undefined,
      salesTaxExemptState: '',
      salesTaxExemptId: '',
      resaleStrategy: '',
      hasPhysicalStore: false,
      whyBusinessAccount: '',
      gradingScaleAcknowledged: undefined as unknown as true,
    },
  });

  const consumerForm = useForm<z.infer<typeof consumerSignupSchema>>({
    resolver: zodResolver(consumerSignupSchema),
    defaultValues: {
      accountType: 'CONSUMER',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
    },
  });

  const signupMutation = useMutation({
    mutationFn: async (data: z.infer<typeof businessSignupSchema> | z.infer<typeof consumerSignupSchema>) => {
      // Upload sales tax exemption file if provided
      let salesTaxExemptUrl = null;
      if (uploadedFile) {
        const formData = new FormData();
        formData.append('file', uploadedFile);
        
        const uploadResponse = await fetch('/api/upload/sales-tax-exempt', {
          method: 'POST',
          body: formData,
        });
        
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload sales tax exemption certificate');
        }
        
        const uploadResult = await uploadResponse.json();
        salesTaxExemptUrl = uploadResult.url;
      }
      
      // Upload store photo if provided
      let storePhotoUrl = null;
      if (storePhotoFile) {
        const formData = new FormData();
        formData.append('file', storePhotoFile);
        
        const uploadResponse = await fetch('/api/upload/store-photo', {
          method: 'POST',
          body: formData,
        });
        
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload store photo');
        }
        
        const uploadResult = await uploadResponse.json();
        storePhotoUrl = uploadResult.url;
      }

      // Submit account application - strip File objects that can't be serialized
      const { salesTaxExemptFile, storePhotoFile: _storePhotoFile, ...submitData } = data as z.infer<typeof businessSignupSchema>;
      return apiRequest('POST', '/api/account-applications', {
        ...submitData,
        salesTaxExemptUrl,
        storePhotoUrl,
      });
    },
    onSuccess: () => {
      setIsSubmitted(true);
    },
    onError: (error: Error) => {
      toast({
        title: 'Submission Failed',
        description: error.message || 'Failed to submit application. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const onBusinessSubmit = (data: z.infer<typeof businessSignupSchema>) => {
    // Validate at least one delivery option is selected ONLY if using corporate as ship-to
    if (data.useCorporateAsShipTo && !data.requiresLiftgate && !data.hasLoadingDock && !data.hasForklift) {
      toast({
        title: 'Validation Error',
        description: 'At least one delivery option must be selected (Liftgate, Loading Dock, or Forklift)',
        variant: 'destructive',
      });
      return;
    }
    
    // Validate sales tax exemption - all three required if any are provided
    const hasState = data.salesTaxExemptState && data.salesTaxExemptState.length > 0;
    const hasId = data.salesTaxExemptId && data.salesTaxExemptId.length > 0;
    const hasFile = !!uploadedFile;
    
    if (hasState || hasId || hasFile) {
      if (!hasState || !hasId || !hasFile) {
        toast({
          title: 'Validation Error',
          description: 'Sales Tax Exemption requires all three fields: State, Tax ID, and Certificate file',
          variant: 'destructive',
        });
        return;
      }
    }
    
    signupMutation.mutate(data);
  };

  const onConsumerSubmit = (data: z.infer<typeof consumerSignupSchema>) => {
    signupMutation.mutate(data);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (PDF, JPG, PNG)
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: 'Invalid File Type',
          description: 'Please upload a PDF, JPG, or PNG file',
          variant: 'destructive',
        });
        return;
      }
      
      // Validate file size (max 25MB)
      if (file.size > 25 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Please upload a file smaller than 25MB',
          variant: 'destructive',
        });
        return;
      }
      
      setUploadedFile(file);
      businessForm.setValue('salesTaxExemptFile', file);
    }
  };

  const handleStorePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (JPG, PNG only for photos)
      const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: 'Invalid File Type',
          description: 'Please upload a JPG, PNG, or WebP image',
          variant: 'destructive',
        });
        return;
      }
      
      // Validate file size (max 10MB for photos)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Please upload an image smaller than 10MB',
          variant: 'destructive',
        });
        return;
      }
      
      setStorePhotoFile(file);
      businessForm.setValue('storePhotoFile', file);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-3 sm:px-4 py-6 sm:py-12">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center p-4 sm:p-6">
            <div className="flex justify-center mb-3 sm:mb-4">
              <CheckCircle2 className="h-12 w-12 sm:h-16 sm:w-16 text-brand-green" />
            </div>
            <CardTitle className="text-xl sm:text-2xl">Application Submitted Successfully!</CardTitle>
            <CardDescription className="text-sm sm:text-lg mt-3 sm:mt-4">
              Thank you for your submission. Our team will review your application and contact you within 3 business days.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-3 sm:space-y-4 p-4 sm:p-6 pt-0">
            <p className="text-xs sm:text-sm text-muted-foreground">
              You will receive an email confirmation shortly. Once your account is approved, you'll be able to log in and start ordering.
            </p>
            <Button onClick={() => window.location.href = '/'} className="mt-3 sm:mt-4 h-10 sm:h-11 text-sm sm:text-base">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show account type selection if not yet selected - Mobile responsive
  if (!accountType) {
    return (
      <div className="min-h-screen bg-background py-6 sm:py-8 lg:py-12 px-3 sm:px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <img 
              src={logoImage} 
              alt="OpenBox by ShopJimmy" 
              className="h-12 sm:h-16 w-auto mx-auto mb-3 sm:mb-4 object-contain"
            />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">Create Your Account</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 sm:mt-2">
              Choose your account type to get started
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
            <Card 
              className="cursor-pointer hover:border-primary transition-colors touch-target" 
              onClick={() => setAccountType('BUSINESS')}
              data-testid="card-select-business"
            >
              <CardHeader className="p-4 sm:p-6">
                <Building2 className="h-10 w-10 sm:h-12 sm:w-12 mb-3 sm:mb-4 text-primary" />
                <CardTitle className="text-base sm:text-lg">Business Account</CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Wholesale pricing, freight shipping, and tax exemption options
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-muted-foreground">
                  <li>• Wholesale pricing</li>
                  <li>• Freight shipping quotes</li>
                  <li>• Sales Tax Exemption</li>
                  <li>• Purchase orders</li>
                </ul>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:border-primary transition-colors hidden" 
              onClick={() => setAccountType('CONSUMER')}
              data-testid="card-select-consumer"
            >
              <CardHeader className="p-4 sm:p-6">
                <User className="h-10 w-10 sm:h-12 sm:w-12 mb-3 sm:mb-4 text-primary" />
                <CardTitle className="text-base sm:text-lg">Consumer Account</CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Simple signup for personal purchases
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-muted-foreground">
                  <li>• Quick registration</li>
                  <li>• Residential delivery</li>
                  <li>• Liftgate service included</li>
                  <li>• Standard pricing</li>
                </ul>
              </CardContent>
            </Card>

            <Link href="/" data-testid="link-browse-first">
              <Card 
                className="cursor-pointer bg-brand-green dark:bg-brand-green hover:bg-brand-green/90 dark:hover:bg-brand-green/90 border-brand-green transition-colors h-full touch-target" 
                data-testid="card-browse-first"
              >
                <CardHeader className="p-4 sm:p-6">
                  <Compass className="h-10 w-10 sm:h-12 sm:w-12 mb-3 sm:mb-4 text-white" />
                  <CardTitle className="text-white text-base sm:text-lg">Just Browsing?</CardTitle>
                  <CardDescription className="text-white/90 text-xs sm:text-sm">
                    No commitment required! Explore our showroom first
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0">
                  <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-white/90">
                    <li>• Browse the full catalog</li>
                    <li>• See pricing & availability</li>
                    <li>• Check out product details</li>
                    <li>• Sign up later when ready</li>
                  </ul>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-6 sm:py-8 lg:py-12 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-6 sm:mb-8">
          <img 
            src={logoImage} 
            alt="OpenBox by ShopJimmy" 
            className="h-12 sm:h-16 w-auto mx-auto mb-3 sm:mb-4 object-contain"
          />
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">
            {accountType === 'BUSINESS' ? 'Business Account Application' : 'Consumer Account Registration'}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 sm:mt-2">
            {accountType === 'BUSINESS' 
              ? 'Complete this form to apply for a wholesale business account'
              : 'Complete this form to create your consumer account'
            }
          </p>
        </div>

        {accountType === 'BUSINESS' ? (
          <Form {...businessForm}>
          <form onSubmit={businessForm.handleSubmit(onBusinessSubmit)} className="space-y-4 sm:space-y-6 lg:space-y-8">
            {/* Company Information - Mobile responsive */}
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Building2 className="h-4 w-4 sm:h-5 sm:w-5" />
                  Company Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6 pt-0">
                <FormField
                  control={businessForm.control}
                  name="legalCompanyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Legal Company Name *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="ABC Appliances LLC" data-testid="input-legal-company-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={businessForm.control}
                  name="dbaName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>DBA Name (if applicable)</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Doing Business As" data-testid="input-dba-name" />
                      </FormControl>
                      <FormDescription>
                        If your business operates under a different name
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={businessForm.control}
                    name="ein"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>EIN (Employer Identification Number)</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="XX-XXXXXXX" data-testid="input-ein" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={businessForm.control}
                    name="companyType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Type *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-company-type">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="llc">LLC</SelectItem>
                            <SelectItem value="corporation">Corporation</SelectItem>
                            <SelectItem value="s-corp">S-Corporation</SelectItem>
                            <SelectItem value="partnership">Partnership</SelectItem>
                            <SelectItem value="sole-proprietorship">Sole Proprietorship</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Primary Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Primary Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={businessForm.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name *</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-first-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={businessForm.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name *</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-last-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={businessForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address *</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" data-testid="input-email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={businessForm.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number *</FormLabel>
                        <FormControl>
                          <Input {...field} type="tel" data-testid="input-phone" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Corporate Address */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Corporate Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={businessForm.control}
                  name="addressLine1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Street Address *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="123 Main Street" data-testid="input-address-line1" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={businessForm.control}
                  name="addressLine2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Line 2</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Suite, Unit, Building" data-testid="input-address-line2" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={businessForm.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City *</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-city" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={businessForm.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State *</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-state" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={businessForm.control}
                    name="zipCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ZIP Code *</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-zip" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator className="my-4" />

                <FormField
                  control={businessForm.control}
                  name="useCorporateAsShipTo"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 bg-muted/30">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="checkbox-use-as-shipto"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="font-medium">
                          Use this address as my default ship-to address
                        </FormLabel>
                        <FormDescription>
                          This will copy your corporate address to your shipping address book during account setup.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                {businessForm.watch("useCorporateAsShipTo") && (
                  <>
                    <Separator className="my-4" />

                    <div className="space-y-4">
                      <div>
                        <h3 className="font-medium">Delivery Equipment Options *</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Select the option that best describes your location. This helps us provide accurate freight quotes.
                        </p>
                      </div>
                      
                      <FormField
                        control={businessForm.control}
                        name="requiresLiftgate"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={(checked) => {
                                  field.onChange(checked);
                                  if (checked) {
                                    businessForm.setValue("hasLoadingDock", false);
                                    businessForm.setValue("hasForklift", false);
                                  }
                                }}
                                data-testid="checkbox-liftgate"
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>
                                Liftgate required (recommended for locations without loading dock/forklift)
                              </FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />

                      <div className="ml-6 space-y-4 border-l-2 pl-4">
                        <p className="text-sm text-muted-foreground">Or, if you have dock/forklift equipment:</p>
                        
                        <FormField
                          control={businessForm.control}
                          name="hasLoadingDock"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={(checked) => {
                                    field.onChange(checked);
                                    if (checked) {
                                      businessForm.setValue("requiresLiftgate", false);
                                    }
                                  }}
                                  data-testid="checkbox-loading-dock"
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  This address has a loading dock
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={businessForm.control}
                          name="hasForklift"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={(checked) => {
                                    field.onChange(checked);
                                    if (checked) {
                                      businessForm.setValue("requiresLiftgate", false);
                                    }
                                  }}
                                  data-testid="checkbox-forklift"
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  This address has forklift equipment
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                      </div>

                      {businessForm.formState.errors.requiresLiftgate && (
                        <p className="text-sm font-medium text-destructive">
                          {businessForm.formState.errors.requiresLiftgate.message}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Company Profile */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Company Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={businessForm.control}
                  name="employeeCount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Number of Employees *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-employee-count">
                            <SelectValue placeholder="Select range" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="1-10">1-10</SelectItem>
                          <SelectItem value="10-50">10-50</SelectItem>
                          <SelectItem value="50-100">50-100</SelectItem>
                          <SelectItem value="100+">100+</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={businessForm.control}
                  name="businessType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-business-type">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="retailer">Retailer</SelectItem>
                          <SelectItem value="distributor">Distributor</SelectItem>
                          <SelectItem value="exporter">Exporter</SelectItem>
                          <SelectItem value="wholesaler">Wholesaler</SelectItem>
                          <SelectItem value="e-commerce">E-commerce</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={businessForm.control}
                  name="annualRevenue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Annual Revenue *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-annual-revenue">
                            <SelectValue placeholder="Select range" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="under-1m">Under $1M</SelectItem>
                          <SelectItem value="1m-5m">$1M - $5M</SelectItem>
                          <SelectItem value="5m-10m">$5M - $10M</SelectItem>
                          <SelectItem value="10m-50m">$10M - $50M</SelectItem>
                          <SelectItem value="50m+">$50M+</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Sales Tax Exemption */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Sales Tax Exemption Certificate
                </CardTitle>
                <CardDescription>
                  If you have a resale certificate, provide your exemption state, tax ID, and upload your certificate. All three fields are required if any are provided.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={businessForm.control}
                    name="salesTaxExemptState"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Exemption State</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-exemption-state">
                              <SelectValue placeholder="Select state" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {US_STATES.map(state => (
                              <SelectItem key={state.code} value={state.code}>
                                {state.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={businessForm.control}
                    name="salesTaxExemptId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sales Tax ID</FormLabel>
                        <FormControl>
                          <Input 
                            {...field} 
                            placeholder="Your resale certificate number"
                            data-testid="input-tax-id"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sales-tax-file">Upload Certificate</Label>
                  <Input
                    id="sales-tax-file"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    data-testid="input-sales-tax-file"
                  />
                  <p className="text-sm text-muted-foreground">
                    PDF, JPG, or PNG - max 25MB
                  </p>
                  {uploadedFile && (
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="h-4 w-4" />
                      Selected: {uploadedFile.name}
                    </p>
                  )}
                </div>

                {businessForm.formState.errors.salesTaxExemptState && (
                  <p className="text-sm font-medium text-destructive">
                    {businessForm.formState.errors.salesTaxExemptState.message}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Business Justification */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Compass className="h-5 w-5" />
                  Business Justification
                </CardTitle>
                <CardDescription>
                  Help us understand your business and why you want a wholesale account with OpenBox by ShopJimmy. This information helps us ensure we're the right fit for your needs.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={businessForm.control}
                  name="whyBusinessAccount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Why do you want a business account with us? *</FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          placeholder="Explain why you're interested in our wholesale pricing and what drew you to OpenBox by ShopJimmy..."
                          data-testid="textarea-why-business-account"
                        />
                      </FormControl>
                      <FormDescription>
                        What attracted you to our products? Why do you want access to wholesale pricing?
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={businessForm.control}
                  name="resaleStrategy"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Describe your resale strategy *</FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          placeholder="How do you plan to resell the products you purchase from us? What channels will you use (online, brick & mortar, marketplaces, etc.)?"
                          data-testid="textarea-resale-strategy"
                        />
                      </FormControl>
                      <FormDescription>
                        Tell us about your sales channels, target customers, and business model.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-4">
                  <FormField
                    control={businessForm.control}
                    name="hasPhysicalStore"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) => {
                              field.onChange(checked);
                              // Clear store photo when unchecked
                              if (!checked) {
                                setStorePhotoFile(null);
                                businessForm.setValue('storePhotoFile', undefined);
                              }
                            }}
                            data-testid="checkbox-has-physical-store"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>I have a physical store or showroom</FormLabel>
                          <FormDescription>
                            Check this if you have a brick-and-mortar location where you sell products
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  {businessForm.watch('hasPhysicalStore') && (
                    <div className="ml-7 space-y-2">
                      <Label htmlFor="store-photo">Upload a photo of your store (optional)</Label>
                      <Input
                        id="store-photo"
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp"
                        onChange={handleStorePhotoChange}
                        data-testid="input-store-photo"
                      />
                      <p className="text-sm text-muted-foreground">
                        JPG, PNG, or WebP - max 10MB
                      </p>
                      {storePhotoFile && (
                        <p className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="h-4 w-4" />
                          Selected: {storePhotoFile.name}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Cosmetic Grading Scale Acknowledgement */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Info className="h-5 w-5" />
                  Cosmetic Grading Scale
                </CardTitle>
                <CardDescription>
                  Please confirm you've reviewed how we grade refrigerator units before submitting.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs sm:text-sm text-amber-900 dark:text-amber-100">
                  <p className="font-semibold mb-1">Refrigerator grades — front-facing cosmetic only:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li><strong>A</strong> — no visible front-facing defects</li>
                    <li><strong>A−</strong> — front previously dented, professionally filled and covered with an automotive-grade vinyl wrap (not the original factory finish)</li>
                    <li><strong>B+</strong> — up to {GRADE_DEFECT_CEILINGS['B+']} small front-facing blemishes</li>
                    <li><strong>B−</strong> — up to {GRADE_DEFECT_CEILINGS['B-']} front-facing blemishes</li>
                  </ul>
                  <p className="mt-2">
                    Every unit is fully functional with the original manufacturer warranty.{' '}
                    <Link href="/grading" className="underline" data-testid="link-business-grading">
                      Read the full grading guide
                    </Link>
                    .
                  </p>
                </div>
                <FormField
                  control={businessForm.control}
                  name="gradingScaleAcknowledged"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start gap-3 space-y-0 rounded-md border p-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value === true}
                          onCheckedChange={(checked) => field.onChange(checked === true ? true : undefined)}
                          data-testid="checkbox-grading-acknowledged"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-tight">
                        <FormLabel className="font-normal cursor-pointer">
                          I understand that B+ and B− refrigerators may have front-facing cosmetic
                          blemishes as described above and that all sales are final. *
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.location.href = '/'}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  signupMutation.isPending ||
                  businessForm.watch('gradingScaleAcknowledged') !== true
                }
                data-testid="button-submit"
              >
                {signupMutation.isPending ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </form>
          </Form>
        ) : (
          <Form {...consumerForm}>
          <form onSubmit={consumerForm.handleSubmit(onConsumerSubmit)} className="space-y-8">
            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={consumerForm.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name *</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-first-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={consumerForm.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name *</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-last-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={consumerForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address *</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" data-testid="input-email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={consumerForm.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number *</FormLabel>
                        <FormControl>
                          <Input {...field} type="tel" data-testid="input-phone" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Residential Address */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Residential Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={consumerForm.control}
                  name="addressLine1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Street Address *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="123 Main Street" data-testid="input-address-line1" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={consumerForm.control}
                  name="addressLine2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Apartment, Suite, etc.</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Apt 4B" data-testid="input-address-line2" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={consumerForm.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City *</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-city" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={consumerForm.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State *</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-state" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={consumerForm.control}
                    name="zipCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ZIP Code *</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-zip" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Cosmetic Grading Scale Notice */}
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4 text-sm text-amber-900 dark:text-amber-100"
              data-testid="notice-consumer-grading"
            >
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold mb-1">Heads up — about our grading scale</p>
                  <p>
                    All refrigerators are fully functional with the original factory warranty.
                    Cosmetic grades describe front-facing surfaces only: <strong>A</strong> = no
                    visible defects, <strong>A−</strong> ={' '}
                    front professionally restored and vinyl-wrapped (not the original factory
                    finish), <strong>B+</strong> = up to {GRADE_DEFECT_CEILINGS['B+']}{' '}
                    small front-facing blemishes, <strong>B−</strong> = up to{' '}
                    {GRADE_DEFECT_CEILINGS['B-']}. See product photos and videos for the actual
                    unit you'll receive.{' '}
                    <Link href="/grading" className="underline" data-testid="link-consumer-grading">
                      Learn more
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.location.href = '/'}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={signupMutation.isPending}
                data-testid="button-submit"
              >
                {signupMutation.isPending ? 'Submitting...' : 'Create Account'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-4">
              By creating an account, you agree to our{' '}
              <a
                href="/terms"
                className="underline hover:text-foreground"
                data-testid="link-signup-terms"
              >
                Terms of Service
              </a>
              , including our cosmetic grading scale.
            </p>
          </form>
          </Form>
        )}
      </div>
    </div>
  );
}
